import React, { useMemo } from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import { List, Map } from 'immutable'
import { connect as connectStaticText } from '../../StaticText/connect'
import AdyenDropIn from '../../AdyenDropIn/index'
import Link, { TARGET_BLANK } from '../../Link/index'
import { getCancelAccountHelpUrl } from '../../../services/url/index'
import { getPrimary } from '../../../services/languages/index'
import CartSinglePageOrderSummary from '../CartSinglePageOrderSummary'
import CartSinglePagePaymentOneClick from './CartSinglePagePaymentOneClick'
import CartSinglePagePaymentPlaceholder from './CartSinglePagePaymentPlaceholder'
import CartSinglePagePaymentSecure from './CartSinglePagePaymentSecure'
import CartSinglePagePaymentError from './CartSinglePagePaymentError'
import { WINBACK_STEPS } from '../utils'

/**
 * Component to handle the payment
 * @param {Map} props.staticText - Static text translations
 * @param {Map} props.checkout - Checkout state
 * @param {Map} props.payment - Payment state
 * @param {Map} props.plans - Plans state
 * @param {Map} props.user - User state
 * @param {Function} props.onEditMail - Function to edit mail
 * @param {Function} props.onChangePlan - Function to change plan
 * @param {Function} props.onCreateAccount - Function to create account
 * @returns {React.ReactNode}
 */
function CartSinglePagePayment (props) {
  const {
    staticText,
    checkout,
    payment,
    plans,
    user,
    onEditMail,
    onChangePlan,
    onCreateAccount,
  } = props

  // Extract data from props
  const selectedPlan = plans.get('selection', Map())
  const planCost = selectedPlan.get('costs', List([0])).first()
  const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))
  const checkoutProcessing = checkout.get('processing')
  const checkoutStep = checkout.getIn(['winback', 'step'])
  const hasSelectedPlan = Map.isMap(selectedPlan) && selectedPlan.size > 0
  const useDefaultPaymentMethod = checkout.getIn(['winback', 'useDefaultPayment']) || false

  // Error handling logic
  const errorState = useMemo(() => {
    const checkoutOrderError = checkout.get('orderError')
    const paymentError = payment.getIn(['data', 'payments', 'success']) === false
      || payment.getIn(['data', 'payments', 'error']) === true
    const paymentValidationError = payment.get('error')

    const errorShouldRender = checkoutOrderError || (paymentError || paymentValidationError)

    return {
      checkoutOrderError,
      paymentError,
      paymentValidationError,
      errorShouldRender,
    }
  }, [checkout, payment])

  /**
   * Renders the payment disclaimer with cancel account link
   */
  const renderDisclaimer = () => {
    return (
      <div className="cart-single-page__payment-disclaimer cart-single-page__disclaimer">
        {`${staticText.getIn(['data', 'disclaimerIntro'])} `}
        <Link
          className="cart-single-page__payment-disclaimer-link"
          directLink
          to={getCancelAccountHelpUrl(userLanguage)}
          target={TARGET_BLANK}
        >
          {`${staticText.getIn(['data', 'disclaimerLink'])}. `}
        </Link>
        {staticText.getIn(['data', 'disclaimerEnd'])}
      </div>
    )
  }

  /**
   * Determines which payment content to render based on current state
   */
  const renderPaymentContent = () => {
    // Show one-click payment if user has saved payment data
    if (useDefaultPaymentMethod) {
      return (
        <CartSinglePagePaymentOneClick
          {...props}
        />
      )
    }

    // Show placeholder if no plan is selected
    if (!hasSelectedPlan) {
      return (
        <CartSinglePagePaymentPlaceholder
          {...props}
          disclaimer={renderDisclaimer()}
        />
      )
    }

    // Show full payment form with Adyen integration
    return (
      <>
        <CartSinglePagePaymentError {...props} />
        <CartSinglePagePaymentSecure staticText={staticText} />
        <AdyenDropIn
          submitButtonText={staticText.getIn(['data', 'startMembership'])}
          disclaimer={renderDisclaimer()}
          parentProcessing={checkoutProcessing}
          error={errorState.errorShouldRender}
          planCost={planCost}
        />
      </>
    )
  }

  return (
    <div className="cart-single-page__payment">
      <div className="cart-single-page__section-title">
        {staticText.getIn(['data', 'setUpYourPayment'])}
      </div>
      <div className="cart-single-page__payment-container">
        <div className="cart-single-page__payment-form">
          {renderPaymentContent()}
        </div>
        <CartSinglePageOrderSummary
          show={checkoutStep === WINBACK_STEPS.SELECT_PAYMENT}
          onChangePlan={onChangePlan}
          onEditMail={onEditMail}
          onCreateAccount={onCreateAccount}
        />
      </div>
    </div>
  )
}

CartSinglePagePayment.defaultProps = {
  visibilityThreshold: 0.6,
  desktopWidth: 1194,
  onChangePlan: () => {},
  onEditMail: () => {},
  onCreateAccount: () => {},
}

export default compose(
  connectStaticText({ storeKey: 'CartSinglePage' }),
  connectRedux(
    (state) => ({
      app: state.app,
      user: state.user,
      checkout: state.checkout,
      plans: state.plans,
      payment: state.payment,
      auth: state.auth,
      userAccount: state.userAccount,
      paytrack: state.paytrack,
    }),
  ),
)(CartSinglePagePayment)
